import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Bot } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

interface Assistant {
  id: string;
  name: string;
  description: string;
  assistant_id: string;
}

interface AssistantSelectorProps {
  selectedAssistant: string;
  onSelectAssistant: (assistantId: string) => void;
}

const AssistantSelector = ({ selectedAssistant, onSelectAssistant }: AssistantSelectorProps) => {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log('[Assistant Selector] Mounted, current selected assistant:', selectedAssistant);
    fetchAssistants();
  }, [selectedAssistant]);

  const fetchAssistants = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('[Assistant Selector] No active session found');
        setIsLoading(false);
        return;
      }

      const { data: assistantsData, error: assistantsError } = await supabase
        .from('assistants')
        .select('*');

      if (assistantsError) throw assistantsError;

      console.log('[Assistant Selector] Fetched assistants:', assistantsData);
      setAssistants(assistantsData || []);
      
      if (assistantsData && assistantsData.length > 0) {
        const isValidSelection = assistantsData.some(assistant => assistant.id === selectedAssistant);
        
        if (!selectedAssistant || !isValidSelection) {
          console.log('[Assistant Selector] Setting initial/default assistant:', {
            id: assistantsData[0].id,
            name: assistantsData[0].name,
            assistant_id: assistantsData[0].assistant_id
          });
          onSelectAssistant(assistantsData[0].id);
          toast.success(`Using ${assistantsData[0].name} as your AI assistant`);
        }
      }
    } catch (error) {
      console.error('[Assistant Selector] Error fetching assistants:', error);
      toast.error("Failed to load assistants. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssistantSelect = (assistant: Assistant) => {
    console.log('[Assistant Selector] Selecting assistant:', {
      id: assistant.id,
      name: assistant.name,
      assistant_id: assistant.assistant_id,
      previousSelection: selectedAssistant
    });
    
    onSelectAssistant(assistant.id);
    toast.success(`Now using ${assistant.name} as your AI assistant`);
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <h2 className="font-semibold px-2">AI Assistants</h2>
        <div className="text-sm text-muted-foreground px-2">Loading assistants...</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h2 className="font-semibold px-2">AI Assistants</h2>
      {assistants.length === 0 ? (
        <div className="text-sm text-muted-foreground px-2">
          No assistants found. Create one in settings.
        </div>
      ) : (
        <div className="space-y-1">
          {assistants.map((assistant) => (
            <Button
              key={assistant.id}
              variant={selectedAssistant === assistant.id ? "secondary" : "ghost"}
              className="w-full justify-start gap-2 min-h-[44px] py-2 px-2"
              onClick={() => handleAssistantSelect(assistant)}
            >
              <Bot className="h-4 w-4 shrink-0" />
              <div className="flex-1 text-left max-w-[calc(100%-2rem)]">
                <div className="font-medium line-clamp-2 text-sm break-all">
                  {assistant.name}
                </div>
              </div>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssistantSelector;